import { ReactElement, useState } from "react";
import AnswerSourceSlider from "./answer-source-slider.component";
import { AnswerSource, AnswerStats, SurveyStatus, SurveyAudienceCriteria, SurveyQuestionType, PulsePollingSurveyDataJSON } from "../../../redux/survey/survey.types";
import { CombinedQuestionStats } from "../view-survey.types";
import { useResultsQuestion } from "./useResultsQuestion";
import AnswerResultBar from "../../../components/survey-results/answer-result-bar";
import { answerTitle, percentageKey, countKey } from "./results-question.helpers";
import { ResultsQuestionListTab } from "./results-question-list.component";
import ResultsQuestionSummary from "./results-question-summary.component";
import ResultsQuestionFilter from "src/pages/survey-results/components/results-question-filter.component";
import FreeformResponseList from "../../../components/survey-results/freeform-response-list.component";
import FeedbackList from "../../../components/survey-results/feedback-list";
import PulsePollingChart from "./pulse-polling-chart.component";
import NormalizationToggle from "./normalization-toggle.component";
import ChartTypeToggle from "./chart-type-toggle.component";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useRef } from "react";

export interface ResultsQuestionProps {
  readonly listTab: ResultsQuestionListTab;
  readonly question: CombinedQuestionStats;
  readonly index: number;
  readonly aiMaxCompletes?: number;
  readonly humanMaxCompletes?: number;
  readonly surveyId: string;
  readonly initialFilter?: SurveyAudienceCriteria;
  readonly surveyStatus?: SurveyStatus;
  readonly pulsePollingData?: PulsePollingSurveyDataJSON;
  readonly aiUserTotal: number;
  readonly humanUserTotal: number;
}

const ResultsQuestion = ({ question, index, aiMaxCompletes, humanMaxCompletes, listTab, surveyId, initialFilter, surveyStatus, pulsePollingData, aiUserTotal, humanUserTotal }: ResultsQuestionProps): ReactElement => {
  const { answerFilter, setAnswerFilter, freeformAnswers, showFilters, hasImageAnswers, maxPercentage, sortedAnswers } = useResultsQuestion(question, aiMaxCompletes, humanMaxCompletes);
  const [isNormalized, setIsNormalized] = useState(false);
  const [isLineChart, setIsLineChart] = useState(false);
  const showCard = showFilters || sortedAnswers.length > 0;

  const onSliderChange = (source: AnswerSource) => {
    setAnswerFilter(source);
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center gap-4 py-6 sticky top-14 bg-gray-200 z-30">
        <div className="flex size-6 shrink-0 rounded-full bg-blue-500 items-center justify-center">
          <div className="text-white">
            {index + 1}
          </div>
        </div>
        <div className="text-3xl">
          {question.title}
        </div>
      </div>
    );
  };

  const renderAnswerCard = () => {
    if (!showCard) {
      return null;
    }

    const usersStarted = answerFilter === AnswerSource.AI ? aiUserTotal :
      answerFilter === AnswerSource.Human ? humanUserTotal :
        aiUserTotal + humanUserTotal;
    const usersCompleted = answerFilter === AnswerSource.AI ? question.unique_ai_users :
      answerFilter === AnswerSource.Human ? question.unique_human_users :
        (question.unique_ai_users ?? 0) + (question.unique_human_users ?? 0);
    const totalCompletes = answerFilter === AnswerSource.AI ? question.aiUsers :
      answerFilter === AnswerSource.Human ? question.humanUsers :
        question.totalUsers;

    const totalRef = useRef<HTMLSpanElement>(null);
    const completedRef = useRef<HTMLSpanElement>(null);
    const startedRef = useRef<HTMLSpanElement>(null);

    useGSAP(() => {
      gsap.to(startedRef.current, {
        duration: 0.5,
        scrambleText: {
          chars: "0123456789",
          text: `${usersStarted}`,
        },
      });
      gsap.to(completedRef.current, {
        duration: 0.5,
        scrambleText: {
          chars: "0123456789",
          text: `${usersCompleted}`,
        },
      });
      gsap.to(totalRef.current, {
        duration: 0.5,
        scrambleText: {
          chars: "0123456789",
          text: `${totalCompletes}`,
        },
      });
    }, [usersCompleted, question.totalUsers]);

    return (
      <div className='flex flex-col bg-black rounded-xl p-4 mx-6 gap-3'>
        <div className="flex items-center justify-between">
          <p className="text-white font-medium rounded-full pl-2 -mb-1 w-full">
            {!pulsePollingData ? (
              <div className="flex flex-col sm:flex-row flex-wrap gap-1 sm:gap-3 mr-2">
                <span>
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  👤 <span ref={completedRef}>{usersCompleted}</span>/<span ref={startedRef}>{question.totalUsers}</span>
                </span>
                <span>
                  {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
                  💬 Type: {question.questionType === SurveyQuestionType.SingleSelect ? 'Single-select' : question.questionType === SurveyQuestionType.MultiSelect ? 'Multi-select' : 'Freeform'}
                </span>
              </div>
            ) : (
              question.questionType !== SurveyQuestionType.Freeform ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <ChartTypeToggle
                    isLineChart={isLineChart}
                    onToggle={setIsLineChart}
                    answerFilter={answerFilter}
                  />
                  <NormalizationToggle
                    isNormalized={isNormalized}
                    onToggle={setIsNormalized}
                    answerFilter={answerFilter}
                  />
                </div>
              ) : (
                <div className="text-center mt-2 w-full">
                  Freeform responses cannot be visualized over time.
                </div>
              )
            )}
          </p>
          {showFilters && !(question.questionType === SurveyQuestionType.Freeform && pulsePollingData) && (
            <div className="flex items-center">
              <AnswerSourceSlider
                initialFilter={answerFilter}
                onChange={onSliderChange}
              />
            </div>
          )}
        </div>
        {!pulsePollingData ? (
          Boolean(sortedAnswers.length) && (
            <div className={`flex flex-col gap-2`}>
              {sortedAnswers.map((answer, index) => renderAnswer(answer, index))}
              <div className="text-sm text-gray-400 mt-1 text-right mr-3">
                <span ref={totalRef}>
                  {usersCompleted}
                </span>
                {' '}
                Total
              </div>
            </div>
          )
        ) : (
          <div className="bg-gray-800 rounded-lg">
            <PulsePollingChart
              data={pulsePollingData}
              answerIndex={index}
              answerFilter={answerFilter}
              isNormalized={isNormalized}
              isLineChart={isLineChart}
            />
          </div>
        )}
      </div>
    );
  };

  const renderAnswer = (answer: AnswerStats, index: number) => {
    const percentage = Number(answer[percentageKey(answerFilter)] ?? 0);
    const count = Number(answer[countKey(answerFilter)] ?? 0);

    return (
      <AnswerResultBar
        extSurveyId={surveyId}
        answerId={answer.id}
        answerSource={answerFilter}
        key={`${answer.title}-${index}`}
        imgSrc={answer.imageUrl}
        totalCount={count}
        answerLabel={answerTitle(answer.title, index, surveyStatus)}
        userPercent={percentage}
        hasImageAnswers={hasImageAnswers}
        useRedesign={true}
      />
    );
  };

  const renderSummary = () => {
    const showForFreeform = freeformAnswers !== undefined && (listTab === ResultsQuestionListTab.Summary || listTab === ResultsQuestionListTab.Responses);
    if (question.summaries === undefined || question.summaries?.length === 0 || (listTab !== ResultsQuestionListTab.Summary && !showForFreeform)) {
      return null;
    }

    return (
      <ResultsQuestionSummary
        questionId={question.id}
        summaries={question.summaries}
        surveyId={surveyId}
      />
    );
  };

  const renderFreeformAnswers = () => {
    if (!freeformAnswers) {
      return null;
    }

    // Don't move the -mb-6 margin into the component. The component renders properly when isolated.
    return (
      <div className="flex flex-col gap-4 -mb-6">
        <div className="font-medium text-lg ml-6">
          Freeform Responses
        </div>
        <FreeformResponseList
          allResponses={freeformAnswers}
          sourceFilter={answerFilter}
          useRedesign
        />
      </div>
    );
  };

  const renderFeedback = () => {
    if (listTab !== ResultsQuestionListTab.Responses || freeformAnswers !== undefined) {
      return null;
    }

    // Don't move the -mb-6 margin into the component. The component renders properly when isolated.
    return (
      <div className="-mb-6">
        <FeedbackList
          answers={sortedAnswers}
          sourceFilter={answerFilter}
          surveyStatus={surveyStatus}
          useRedesign
        />
      </div>
    );
  };

  const renderDemographics = () => {
    if (listTab !== ResultsQuestionListTab.Demographics || freeformAnswers !== undefined) {
      return null;
    }

    return (
      <div className="px-6">
        <ResultsQuestionFilter
          sourceFilter={answerFilter}
          initialFilter={initialFilter}
          stats={question}
          surveyStatus={surveyStatus}
        />
      </div>
    );
  };

  return (
    <div
      className="flex flex-col scroll-m-14"
      id={`results-question-${index + 1}`}
    >
      {renderHeader()}
      <div className="flex flex-col rounded-2xl bg-white gap-6 py-6">
        {renderAnswerCard()}
        {!pulsePollingData && renderFreeformAnswers()}
        {!pulsePollingData && renderFeedback()}
        {!pulsePollingData && renderSummary()}
        {!pulsePollingData && renderDemographics()}
      </div>
    </div>
  );
};

export default ResultsQuestion;
